// The canonical list of platforms socialEntries can hold -- the single
// source both the server and the client validate/build against, so
// adding a platform never means updating two independent lists that can
// drift out of sync (see route/api/users.js's old comment on SOCIAL_KEYS
// before this file existed).
//
// Lives under client/src/ so CRA's webpack build can `import` it
// normally, but it's required directly by server code too (see
// utils/socialEntries.js) via a plain relative path -- Node has no
// restriction on requiring files from anywhere on disk, only CRA's own
// ModuleScopePlugin restricts the *client* bundle to files under
// client/src/, and this file already is one. That's why this is
// CommonJS (module.exports), not an ES module: plain Node (no build
// step) needs to `require()` it as-is, and webpack/Babel's CommonJS
// interop makes `import { X } from './socialPlatformRegistry'` resolve
// fine from the client side regardless.
//
// This list only decides *validity* -- whether a platform is one
// socialEntries can hold and, via urlTemplate, how its link is built.
// It does not decide display metadata (label, icon) -- that's
// socialPlatforms.js, layered on top of this same list, client-only. It
// also has nothing to do with the legacy `social` object in
// models/User.js, which keeps its own fixed 25 keys forever -- see
// LEGACY_SOCIAL_SCHEMA_KEYS in utils/socialEntries.js. A platform can
// exist here and be fully functional in socialEntries while having no
// slot in `social` at all; projectEntriesToSocial already skips any
// platform without a legacy key, precisely so this list is free to grow
// without that projection needing to know or care.
//
// Adding a platform: one entry here (key + urlTemplate), one metadata
// entry in socialPlatforms.js (its display label, an example value, and
// a one-sentence hint), and one icon file at
// public/images/social-network-<key>.png. Nothing else -- no schema
// change, no backfill; an existing user is unaffected until they
// actually add one themselves.
const SOCIAL_PLATFORM_LIST = [
  { key: 'instagram', urlTemplate: (v) => `https://instagram.com/${v}` },
  { key: 'snapchat', urlTemplate: (v) => `https://snapchat.com/add/${v}` },
  { key: 'whatsapp', urlTemplate: (v) => `https://wa.me/${v}` },
  { key: 'whatsappbusiness', urlTemplate: (v) => `https://wa.me/${v}` },
  { key: 'phone', urlTemplate: (v) => `tel:${v}` },
  { key: 'landline', urlTemplate: (v) => `tel:${v}` },
  { key: 'twitter', urlTemplate: (v) => `https://twitter.com/${v}` },
  { key: 'facebook', urlTemplate: (v) => `https://facebook.com/${v}` },
  { key: 'linkedin', urlTemplate: (v) => `${v}` },
  { key: 'youtube', urlTemplate: (v) => `${v}` },
  { key: 'tiktok', urlTemplate: (v) => `${v}` },
  { key: 'twitch', urlTemplate: (v) => `https://twitch.com/${v}` },
  { key: 'telegram', urlTemplate: (v) => `https://t.me/${v}` },
  { key: 'pinterest', urlTemplate: (v) => `https://pinterest.com/${v}` },
  { key: 'applemusic', urlTemplate: (v) => `https://applemusic.com/${v}` },
  { key: 'spotify', urlTemplate: (v) => `https://open.spotify.com/${v}` },
  { key: 'soundcloud', urlTemplate: (v) => `https://soundcloud.com/${v}` },
  { key: 'website', urlTemplate: (v) => `https://${v}` },
  { key: 'link', urlTemplate: (v) => `https://${v}` },
  { key: 'calendly', urlTemplate: (v) => `https://calendly.com/${v}` },
  { key: 's_email', urlTemplate: (v) => `mailto:${v}` },
  { key: 'address', urlTemplate: (v) => `${v}` },
  { key: 'skype', urlTemplate: (v) => `${v}` },

  // Added after the shared-list refactor -- none of these have a slot in
  // the legacy `social` schema (models/User.js), so projectEntriesToSocial
  // always skips them; they exist only in socialEntries.
  { key: 'discord', urlTemplate: (v) => `https://discord.com/users/${v}` },
  // WeChat has no public profile URL scheme at all -- contact happens by
  // scanning a QR code or searching an ID inside the app. Passed through
  // as-is, same as skype/linkedin/youtube/tiktok below: the value is
  // whatever the user pastes (their WeChat ID, or a QR/profile link if
  // they have one), not something this app can construct a URL from.
  { key: 'wechat', urlTemplate: (v) => `${v}` },
  { key: 'threads', urlTemplate: (v) => `https://www.threads.net/@${v}` },
  { key: 'x', urlTemplate: (v) => `https://x.com/${v}` },
  // Signal's own documented deep-link scheme for a phone-number contact.
  // Value is expected to be the full E.164 number (with country code),
  // same assumption phone/whatsapp already make.
  { key: 'signal', urlTemplate: (v) => `https://signal.me/#p/${v}` },
  // Upwork's public freelancer profile URL isn't a plain username -- it's
  // the "~<id>" segment Upwork itself generates (visible in the user's
  // own profile URL, tilde included). The value is expected to already
  // be that segment.
  { key: 'upwork', urlTemplate: (v) => `https://www.upwork.com/freelancers/${v}` },
  { key: 'fiverr', urlTemplate: (v) => `https://www.fiverr.com/${v}` },
  { key: 'behance', urlTemplate: (v) => `https://www.behance.net/${v}` },

  // ------------------------------------------------------------------
  // PAYMENT METHODS
  //
  // Two things are true of this block at once, and they are deliberately
  // tracked by two different fields:
  //
  //   group: 'payment'  is what a *user* thinks these are. All ten read
  //                     as "ways to pay me" and belong under one heading
  //                     in the picker.
  //   kind              is what the *app* does with them. The five
  //                     Pakistani entries display a value to copy
  //                     (kind: 'payment'); the five international ones
  //                     open a URL built from a handle and are ordinary
  //                     links (no kind, so LINK_KIND).
  //
  // Splitting these was the point. Grouping by kind put the "Payment
  // methods" heading in the middle of the payment methods, with Cash App,
  // Revolut and Wise stranded above it among the socials. Overloading
  // kind to fix that would have been worse: kind decides QR rendering,
  // clipboard behaviour and whether validatePaymentValue runs, so
  // widening it to move a heading would have started demanding a
  // Pakistani mobile number for a Wise handle.
  //
  // Order here is the order the picker renders, which is why the block is
  // contiguous and sorted deliberately: Pakistani methods first, since
  // this is a Pakistani product and they are what most users want, then
  // the international ones. Nothing else derives an order from this list
  // (SOCIAL_PLATFORM_KEYS is only ever used with .includes(), and the
  // legacy projection walks LEGACY_SOCIAL_SCHEMA_KEYS), so the sequence
  // here is free to mean exactly this.
  //
  // Raast is the unifier among the Pakistani five: a Raast ID is a mobile
  // number linked to a bank account and settles across every Pakistani
  // bank, so there is deliberately no per-bank entry -- Raast ID plus
  // IBAN covers all of them. The three wallets are separate because
  // customers ask for them by name.
  //
  // urlTemplate is identity for the copy-type entries: there is no URL to
  // build. It stays present rather than null so every consumer that maps
  // over this list keeps working without a kind check.
  { key: 'raast', urlTemplate: (v) => `${v}`, kind: 'payment', group: 'payment' },
  { key: 'iban', urlTemplate: (v) => `${v}`, kind: 'payment', group: 'payment' },
  { key: 'easypaisa', urlTemplate: (v) => `${v}`, kind: 'payment', group: 'payment' },
  { key: 'jazzcash', urlTemplate: (v) => `${v}`, kind: 'payment', group: 'payment' },
  { key: 'nayapay', urlTemplate: (v) => `${v}`, kind: 'payment', group: 'payment' },

  // International. Ordinary link entries -- each has a real public URL
  // built from a handle, so there is nothing to copy, no QR and nothing
  // to validate. They carry no `kind`, which keeps them out of
  // isPaymentPlatform and therefore out of validatePaymentValue: a
  // handle is not a Pakistani mobile number and must not be asked to be.
  //
  // Every template was checked against the live site rather than taken
  // from documentation, because these change -- paypal's was wrong in
  // exactly that way (paypal.com/<user> 404s; the PayPal.Me link it
  // always meant is paypal.me/<user>). cash.app/<tag> redirects to
  // cash.app/$<tag>, so the $ belongs to the URL; revolut.me is a
  // client-rendered SPA whose router declares path:"/:username", a bare
  // handle with no @; wise.com/pay/me/ is a Next.js "/pay/me/[nickname]".
  //
  // Payoneer is deliberately absent. Its payment links are per-request
  // objects -- created with an amount, description and due date, and
  // immutable once shared -- not a permanent public handle, so there is
  // no stable URL to build from a username.
  { key: 'paypal', urlTemplate: (v) => `https://paypal.me/${v}`, group: 'payment' },
  { key: 'venmo', urlTemplate: (v) => `https://venmo.com/u/${v}`, handlePrefix: '@', group: 'payment' },
  { key: 'cashapp', urlTemplate: (v) => `https://cash.app/$${v}`, handlePrefix: '$', group: 'payment' },
  { key: 'revolut', urlTemplate: (v) => `https://revolut.me/${v}`, handlePrefix: '@', group: 'payment' },
  { key: 'wise', urlTemplate: (v) => `https://wise.com/pay/me/${v}`, handlePrefix: '@', group: 'payment' },
];

// Everything without an explicit kind opens a link.
const LINK_KIND = 'link';
const PAYMENT_KIND = 'payment';

const PLATFORM_KIND_BY_KEY = SOCIAL_PLATFORM_LIST.reduce((acc, p) => {
  acc[p.key] = p.kind || LINK_KIND;
  return acc;
}, {});

const PAYMENT_PLATFORM_KEYS = SOCIAL_PLATFORM_LIST.filter(
  (p) => p.kind === PAYMENT_KIND
).map((p) => p.key);

// 'custom' isn't in the list above and is always a link, so the fallback
// covers it without a special case.
const platformKind = (key) => PLATFORM_KIND_BY_KEY[key] || LINK_KIND;

const isPaymentPlatform = (key) => platformKind(key) === PAYMENT_KIND;

// Handle prefixes. Several providers *display* a handle with a leading
// symbol -- Cash App's $cashtag, Venmo's, Revolut's and Wise's @handle --
// that is not part of the URL path (except Cash App's, where it is, which
// is exactly why this cannot be guessed per-platform at the call site).
// Someone copying their handle off the provider's own screen copies the
// symbol with it, and pasting that into a template that also supplies one
// produces cash.app/$$name or venmo.com/u/@name: a link that 404s and
// looks like the user's own typo.
//
// Stripped on save (route/api/users.js) rather than papered over at
// render time, so what is stored is the bare handle every consumer can
// rely on -- the vCard, the public profile and the editor all read the
// same value and none of them needs to know about the symbol.
//
// Declared per platform rather than hardcoded so adding a provider with
// its own symbol stays a one-line change, like urlTemplate itself.
const PLATFORM_HANDLE_PREFIX_BY_KEY = SOCIAL_PLATFORM_LIST.reduce((acc, p) => {
  if (p.handlePrefix) acc[p.key] = p.handlePrefix;
  return acc;
}, {});

// Trims, then strips every leading copy of the platform's symbol -- a
// loop rather than a single slice because "@@name" is a paste slip that
// should still resolve, and because stripping one of two would leave a
// value that looks deliberate. Platforms without a symbol are trimmed
// only, so this is safe to call for any platform, including 'custom'.
const normalizeHandle = (key, value) => {
  const trimmed = String(value == null ? '' : value).trim();
  const prefix = PLATFORM_HANDLE_PREFIX_BY_KEY[key];
  if (!prefix) return trimmed;

  let result = trimmed;
  while (result.charAt(0) === prefix) {
    result = result.slice(1);
  }
  return result;
};

// Presentation grouping, kept strictly separate from `kind`. This decides
// only which heading a platform appears under in the picker; it grants no
// behaviour and gates nothing. Anything that changes what the app *does*
// -- QR rendering, clipboard vs link, server-side value validation --
// reads kind/isPaymentPlatform instead, and those still describe exactly
// the five Pakistani platforms.
//
// Derived from the list rather than written out again, so a platform
// added with group: 'payment' joins the heading (in its list position)
// without anyone remembering to update a second array.
const PAYMENT_GROUP = 'payment';

const PAYMENT_GROUP_KEYS = SOCIAL_PLATFORM_LIST.filter(
  (p) => p.group === PAYMENT_GROUP
).map((p) => p.key);

const inPaymentGroup = (key) => PAYMENT_GROUP_KEYS.indexOf(key) !== -1;

const SOCIAL_PLATFORM_KEYS = SOCIAL_PLATFORM_LIST.map((p) => p.key);

const SOCIAL_PLATFORM_URL_TEMPLATE_BY_KEY = SOCIAL_PLATFORM_LIST.reduce((acc, p) => {
  acc[p.key] = p.urlTemplate;
  return acc;
}, {});

module.exports = {
  SOCIAL_PLATFORM_LIST,
  SOCIAL_PLATFORM_KEYS,
  SOCIAL_PLATFORM_URL_TEMPLATE_BY_KEY,
  PAYMENT_PLATFORM_KEYS,
  PAYMENT_GROUP_KEYS,
  PAYMENT_GROUP,
  inPaymentGroup,
  PLATFORM_KIND_BY_KEY,
  PLATFORM_HANDLE_PREFIX_BY_KEY,
  normalizeHandle,
  platformKind,
  isPaymentPlatform,
  LINK_KIND,
  PAYMENT_KIND,
};
